/**
 * JSON-LD structured-data builders for the marketing site.
 *
 * Static blobs are exported as constants. Page-specific structures (breadcrumb,
 * FAQ) are exported as builder functions.
 *
 * All entries follow schema.org conventions. See https://schema.org for
 * the full vocabulary.
 */

const ORIGIN = 'https://www.noxverify.com';

export const organizationLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'NoxVerify',
  url: ORIGIN,
  logo: `${ORIGIN}/og-image.png`,
  sameAs: [
    'https://www.linkedin.com/company/noxverify',
    'https://twitter.com/noxverify',
    'https://github.com/noxverify',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'sales',
    email: 'sales@noxverify.com',
  },
} as const;

export const websiteLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'NoxVerify',
  url: ORIGIN,
} as const;

export const softwareApplicationLd = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'NoxVerify',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  // NOTE: An `offers` block with `price: "0"` was intentionally removed. NoxVerify
  // is an enterprise B2B compliance platform with custom/quote-based pricing; a
  // hardcoded "$0" falsely signalled a free product and risked surfacing as "$0"
  // in rich results. Pricing is deliberately omitted rather than faked.
} as const;

// ---------------------------------------------------------------------------
// Service schemas (KYC / KYB / KYM)
//
// These give AI search engines and answer engines an explicit, machine-readable
// description of each offering. Descriptions are capability-based and factual —
// no fabricated certifications, dates, or guarantees.
// ---------------------------------------------------------------------------

const serviceProvider = {
  '@type': 'Organization',
  name: 'NoxVerify',
  url: ORIGIN,
} as const;

export const kycServiceLd = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: 'Know Your Customer (KYC) identity verification',
  name: 'NoxVerify KYC',
  provider: serviceProvider,
  areaServed: 'Worldwide',
  description:
    'Automated customer identity verification combining document OCR, biometric liveness matching, and sanctions/PEP screening with configurable automated decisioning.',
  url: `${ORIGIN}/#features`,
  audience: {
    '@type': 'BusinessAudience',
    audienceType: 'Regulated financial institutions, fintechs, and marketplaces',
  },
} as const;

export const kybServiceLd = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: 'Know Your Business (KYB) business verification',
  name: 'NoxVerify KYB',
  provider: serviceProvider,
  areaServed: 'Worldwide',
  description:
    'Automated business verification with ultimate beneficial owner (UBO) resolution, corporate registry checks, ownership-graph traversal, and adverse-media screening of entities and their controllers.',
  url: `${ORIGIN}/#features`,
  audience: {
    '@type': 'BusinessAudience',
    audienceType: 'Regulated financial institutions, fintechs, and marketplaces',
  },
} as const;

export const kymServiceLd = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: 'Know Your Merchant (KYM) merchant monitoring',
  name: 'NoxVerify KYM',
  provider: serviceProvider,
  areaServed: 'Worldwide',
  description:
    'Continuous merchant risk monitoring with scheduled and event-driven rescreening against updated sanctions, PEP, and adverse-media sources to surface emerging risk after onboarding.',
  url: `${ORIGIN}/#features`,
  audience: {
    '@type': 'BusinessAudience',
    audienceType: 'Payment processors, acquirers, and marketplaces',
  },
} as const;

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export function breadcrumbLd(items: readonly BreadcrumbItem[]): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export interface FaqItem {
  q: string;
  a: string;
}

export function faqLd(items: readonly FaqItem[]): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };
}

export interface ArticleAuthorInput {
  name: string;
  jobTitle?: string;
  url?: string;
}

export interface ArticleLdInput {
  headline: string;
  description?: string;
  /** Per-article image; falls back to the global og-image when absent. */
  image?: string;
  datePublished?: string;
  /** Distinct last-modified date; falls back to datePublished only when absent. */
  dateModified?: string;
  /** Structured author (Person). Falls back to the NoxVerify Organization. */
  author?: ArticleAuthorInput;
  url: string;
}

/**
 * Builds a schema.org `BlogPosting` for an individual blog article.
 *
 * Emits proper author attribution (Person with optional jobTitle/url), a
 * publisher Organization with a logo, `mainEntityOfPage`, and a
 * `SpeakableSpecification` so voice assistants can read the headline + lede.
 */
export function articleLd(input: ArticleLdInput): object {
  const image = input.image
    ? input.image.startsWith('http')
      ? input.image
      : `${ORIGIN}${input.image}`
    : `${ORIGIN}/og-image.png`;

  const author = input.author
    ? {
        '@type': 'Person',
        name: input.author.name,
        ...(input.author.jobTitle ? { jobTitle: input.author.jobTitle } : {}),
        ...(input.author.url ? { url: input.author.url } : {}),
      }
    : { '@type': 'Organization', name: 'NoxVerify' };

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: input.headline,
    description: input.description,
    image,
    datePublished: input.datePublished,
    dateModified: input.dateModified ?? input.datePublished,
    author,
    publisher: {
      '@type': 'Organization',
      name: 'NoxVerify',
      logo: { '@type': 'ImageObject', url: `${ORIGIN}/og-image.png` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': input.url },
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['h1', '.article-lede'],
    },
  };
}
